import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { HelpBalloon } from '../../help/HelpBalloon'
import { getProgram } from '../../api/programs'
import { getAccount } from '../../api/accounts'
import { getLegalEntity } from '../../api/legalEntities'
import { useDashboardUrlState } from './filter/useDashboardUrlState'
import { FilterChipBar } from './filter/FilterChipBar'
import { toDashboardFilter } from './filter/dashboardFilterReducer'
import { useDashboardFilterOptions } from './filter/useDashboardFilterOptions'
import { TypeaheadPicker } from './filter/TypeaheadPicker'
import { searchAccounts, searchLegalEntities, searchPrograms } from './filter/filterSearchSources'
import { DashboardGrid } from './grid/DashboardGrid'
import { buildDashboardSections } from './dashboardWidgetRegistry'
import type { WidgetProps } from './widgets/types'
import { DashboardThemePicker } from '../../theme/DashboardThemePicker'
import type { DashboardFilter } from '../../types/insights'
import './charts/charts.css'

const AXIS_LABELS: Record<string, string> = {
  fiscalYearId: 'Fiscal year',
  jurisdiction: 'Jurisdiction',
  accountId: 'Account',
  legalEntityId: 'Legal entity',
  kind: 'Kind',
  programId: 'Program',
  currentState: 'Status',
  territory: 'Territory',
}

/**
 * `/insights/dashboards` — replaces the `ComingSoonStub` (M23.5). Every widget fetches
 * independently (AC26): one tile's `error`/`not_authorized`/`coming_soon` never blanks the rest of
 * this screen — each is rendered by `WidgetFrame`, which owns exactly that isolation.
 *
 * **IB-311 Slice 1** re-platformed the tile layout onto `DashboardGrid` + `dashboardWidgetRegistry`
 * (a data-driven 12-col grid with real section hierarchy and per-widget auto-density — see both
 * modules' own doc comments) and replaced the raw-id filter inputs with real pickers
 * (`useDashboardFilterOptions` + `TypeaheadPicker`). No widget's own data contract changed.
 */
export function DashboardScreen() {
  const navigate = useNavigate()
  const { state, dispatch, unknownKeys, noticeDismissed, dismissNotice } = useDashboardUrlState()
  const [filterFieldsOpen, setFilterFieldsOpen] = useState(false)

  const filter: DashboardFilter = toDashboardFilter(state)

  function onDrill(rsql: string) {
    navigate(`/cases/filings${rsql ? `?filter=${encodeURIComponent(rsql)}` : ''}`)
  }

  const widgetProps = (widgetKey: string): WidgetProps => ({
    filter,
    skipRefetch: state.crossFilterOrigin === widgetKey,
    onCrossFilter: (axis, value) => dispatch({ type: 'ADD_CROSSFILTER', axis, value, sourceWidget: widgetKey }),
    onDrill,
  })

  const sections = buildDashboardSections({
    filter,
    widgetProps,
    skipRefetchFor: (widgetKey) => state.crossFilterOrigin === widgetKey,
  })

  return (
    <div className="iw-dashboard-screen">
      <div className="iw-dashboard-header">
        <span className="iw-dashboard-header__icon">
          <Icon name="bar-chart" size={20} />
        </span>
        <h1 className="iw-dashboard-header__title">Dashboards</h1>
        <HelpBalloon id="list.insights" />
        <span className="iw-dashboard-header__spacer" />
        <button
          type="button"
          className="iw-btn iw-btn--sm"
          aria-expanded={filterFieldsOpen}
          onClick={() => setFilterFieldsOpen((v) => !v)}
        >
          <Icon name="filter" size={14} />
          Filters
        </button>
        <DashboardThemePicker />
        <span title="PDF export ships with the render pipeline (IB-147).">
          <button type="button" className="iw-btn iw-btn--sm" disabled aria-disabled="true">
            <Icon name="download" size={14} />
            Export PDF — coming soon
          </button>
        </span>
      </div>

      {filterFieldsOpen ? <DashboardFilterFields state={state} dispatch={dispatch} /> : null}

      {unknownKeys.length > 0 && !noticeDismissed ? (
        <div className="iw-dashboard-notice">
          <InlineAlert
            severity="warning"
            action={
              <button type="button" className="iw-btn iw-btn--sm" onClick={dismissNotice}>
                Dismiss
              </button>
            }
          >
            Some filters in this link could not be applied and were ignored.
          </InlineAlert>
        </div>
      ) : null}

      <FilterChipBar state={state} dispatch={dispatch} axisLabels={AXIS_LABELS} />

      <DashboardGrid sections={sections} />
    </div>
  )
}

interface DashboardFilterFieldsProps {
  state: ReturnType<typeof useDashboardUrlState>['state']
  dispatch: ReturnType<typeof useDashboardUrlState>['dispatch']
}

/**
 * Real dimension pickers (IB-311 Slice 1) — replaces the former raw-id text inputs. Fiscal year /
 * jurisdiction / filing status are plain `<select>`s populated by `useDashboardFilterOptions`
 * (derived from data the dashboard already fetches — no new backend endpoint); program / account /
 * legal entity are `TypeaheadPicker`s backed by the existing `listPrograms`/`listAccounts`/
 * `listLegalEntities` reads. Every field still dispatches the same `SET_FILTER` action into the
 * same URL/cross-filter/AND-compose machinery as before — only the input affordance changed.
 */
function DashboardFilterFields({ state, dispatch }: DashboardFilterFieldsProps) {
  const options = useDashboardFilterOptions()
  const [resolvedLabels, setResolvedLabels] = useState<Partial<Record<'programId' | 'accountId' | 'legalEntityId', string>>>({})

  const programId = state.filters.programId
  const accountId = state.filters.accountId
  const legalEntityId = state.filters.legalEntityId

  // Best-effort reverse lookup (existing get-by-id reads — no new backend) so a filter restored
  // from a shared URL shows its real name/code instead of a bare id the first time it renders.
  useEffect(() => {
    if (!programId) return
    const controller = new AbortController()
    getProgram(programId, controller.signal)
      .then((p) => setResolvedLabels((prev) => ({ ...prev, programId: p.code })))
      .catch(() => undefined)
    return () => controller.abort()
  }, [programId])

  useEffect(() => {
    if (!accountId) return
    const controller = new AbortController()
    getAccount(accountId, controller.signal)
      .then(({ account }) => setResolvedLabels((prev) => ({ ...prev, accountId: account.name })))
      .catch(() => undefined)
    return () => controller.abort()
  }, [accountId])

  useEffect(() => {
    if (!legalEntityId) return
    const controller = new AbortController()
    getLegalEntity(legalEntityId, controller.signal)
      .then(({ entity }) => setResolvedLabels((prev) => ({ ...prev, legalEntityId: entity.name })))
      .catch(() => undefined)
    return () => controller.abort()
  }, [legalEntityId])

  function setFilter(axis: keyof DashboardFilter, value: string | undefined) {
    dispatch({ type: 'SET_FILTER', axis, value: value || undefined })
  }

  return (
    <div className="iw-dashboard-filter-fields">
      <div className="iw-field">
        <label className="iw-field-label" htmlFor="dashboard-filter-fiscalYearId">
          {AXIS_LABELS.fiscalYearId}
        </label>
        <select
          id="dashboard-filter-fiscalYearId"
          className="iw-select"
          value={state.filters.fiscalYearId ?? ''}
          onChange={(e) => setFilter('fiscalYearId', e.target.value)}
        >
          <option value="">Any fiscal year</option>
          {options.fiscalYears.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="iw-field">
        <label className="iw-field-label" htmlFor="dashboard-filter-jurisdiction">
          {AXIS_LABELS.jurisdiction}
        </label>
        <select
          id="dashboard-filter-jurisdiction"
          className="iw-select"
          value={state.filters.jurisdiction ?? ''}
          onChange={(e) => setFilter('jurisdiction', e.target.value)}
        >
          <option value="">Any jurisdiction</option>
          {options.jurisdictions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="iw-field">
        <label className="iw-field-label" htmlFor="dashboard-filter-currentState">
          {AXIS_LABELS.currentState}
        </label>
        <select
          id="dashboard-filter-currentState"
          className="iw-select"
          value={state.filters.currentState ?? ''}
          onChange={(e) => setFilter('currentState', e.target.value)}
        >
          <option value="">Any status</option>
          {options.statuses.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <TypeaheadPicker
        id="dashboard-filter-programId"
        label={AXIS_LABELS.programId ?? 'Program'}
        placeholder="Search programs by code…"
        value={programId}
        valueLabel={resolvedLabels.programId}
        onChange={(value) => setFilter('programId', value)}
        search={searchPrograms}
      />

      <TypeaheadPicker
        id="dashboard-filter-accountId"
        label={AXIS_LABELS.accountId ?? 'Account'}
        placeholder="Search clients by name…"
        value={accountId}
        valueLabel={resolvedLabels.accountId}
        onChange={(value) => setFilter('accountId', value)}
        search={searchAccounts}
      />

      <TypeaheadPicker
        id="dashboard-filter-legalEntityId"
        label={AXIS_LABELS.legalEntityId ?? 'Legal entity'}
        placeholder="Search legal entities by name…"
        value={legalEntityId}
        valueLabel={resolvedLabels.legalEntityId}
        onChange={(value) => setFilter('legalEntityId', value)}
        search={searchLegalEntities}
      />
    </div>
  )
}
